import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Field,
  TextInput,
  Select,
  formatCurrency,
} from "@odingard/design-system";
import { EmptyState, ErrorState, LoadingState } from "@odingard/design-system";
import type { OpportunityInput } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { proposalApi } from "../../lib/api";
import { DemoModeBanner } from "../../components/ProposalGrounded";

const OPPORTUNITY_TYPES = ["RFP", "RFQ", "RFI", "IDIQ", "BPA", "Sources Sought", "Task Order", "Other"];

interface FormState {
  title: string;
  customer: string;
  agency: string;
  opportunityNumber: string;
  opportunityType: string;
  estimatedValue: string;
  setAside: string;
  naics: string;
  psc: string;
  contractVehicle: string;
  questionsDueDate: string;
  proposalDueDate: string;
}

const EMPTY_FORM: FormState = {
  title: "",
  customer: "",
  agency: "",
  opportunityNumber: "",
  opportunityType: "",
  estimatedValue: "",
  setAside: "",
  naics: "",
  psc: "",
  contractVehicle: "",
  questionsDueDate: "",
  proposalDueDate: "",
};

export function PaiOpportunities() {
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const state = useAsync(async () => proposalApi.opportunities(orgId), [orgId]);

  function set<K extends keyof FormState>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function create() {
    if (!form.title.trim()) return;
    const trimmedValue = form.estimatedValue.trim();
    const dollars = trimmedValue === "" ? null : Number(trimmedValue);
    if (dollars !== null && !Number.isFinite(dollars)) return;
    // estimatedValue is stored/displayed in minor units (formatCurrency divides
    // by 100), so convert the whole-dollar input the user typed into cents.
    const numericValue = dollars === null ? null : Math.round(dollars * 100);
    const clean = (v: string) => {
      const t = v.trim();
      return t === "" ? null : t;
    };
    const input: OpportunityInput = {
      title: form.title.trim(),
      customer: clean(form.customer),
      agency: clean(form.agency),
      opportunityNumber: clean(form.opportunityNumber),
      estimatedValue: numericValue,
      setAside: clean(form.setAside),
      naics: clean(form.naics),
      psc: clean(form.psc),
      contractVehicle: clean(form.contractVehicle),
      questionsDueDate: clean(form.questionsDueDate),
      proposalDueDate: clean(form.proposalDueDate),
    };
    const type = form.opportunityType.trim();
    if (type !== "") input.opportunityType = type;
    setSaving(true);
    try {
      await proposalApi.createOpportunity(orgId, input);
      setForm(EMPTY_FORM);
      setExpanded(false);
      state.reload();
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Opportunity Pipeline</h1>
          <p>Track pursuits from identification through award.</p>
        </div>
      </div>

      <DemoModeBanner show={state.data?.demoMode ?? false} />

      <Card>
        <CardHeader
          title="Add an opportunity"
          action={
            <Button variant="ghost" size="sm" onClick={() => setExpanded((v) => !v)}>
              {expanded ? "Fewer details" : "More details"}
            </Button>
          }
        />
        <CardBody>
          <div className="ods-stack" style={{ gap: "var(--ods-space-3)" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "var(--ods-space-3)",
              }}
            >
              <Field label="Title">
                {(id) => (
                  <TextInput
                    id={id}
                    value={form.title}
                    onChange={(e) => set("title", e.target.value)}
                    placeholder="e.g. Enterprise Cloud Modernization"
                  />
                )}
              </Field>
              <Field label="Customer">
                {(id) => (
                  <TextInput
                    id={id}
                    value={form.customer}
                    onChange={(e) => set("customer", e.target.value)}
                    placeholder="e.g. Northwind Federal"
                  />
                )}
              </Field>
              <Field label="Agency">
                {(id) => (
                  <TextInput
                    id={id}
                    value={form.agency}
                    onChange={(e) => set("agency", e.target.value)}
                    placeholder="e.g. GSA"
                  />
                )}
              </Field>
              <Field label="Solicitation / opportunity #">
                {(id) => (
                  <TextInput
                    id={id}
                    value={form.opportunityNumber}
                    onChange={(e) => set("opportunityNumber", e.target.value)}
                    placeholder="e.g. 47QTCA24R0001"
                  />
                )}
              </Field>
              <Field label="Type">
                {(id) => (
                  <Select
                    id={id}
                    value={form.opportunityType}
                    onChange={(e) => set("opportunityType", e.target.value)}
                  >
                    <option value="">Select type…</option>
                    {OPPORTUNITY_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label="Estimated value (USD)">
                {(id) => (
                  <TextInput
                    id={id}
                    type="number"
                    min="0"
                    step="1000"
                    value={form.estimatedValue}
                    onChange={(e) => set("estimatedValue", e.target.value)}
                    placeholder="e.g. 1800000"
                  />
                )}
              </Field>
              {expanded && (
                <>
                  <Field label="Set aside">
                    {(id) => (
                      <TextInput
                        id={id}
                        value={form.setAside}
                        onChange={(e) => set("setAside", e.target.value)}
                        placeholder="e.g. 8(a), SDVOSB, Full & Open"
                      />
                    )}
                  </Field>
                  <Field label="NAICS">
                    {(id) => (
                      <TextInput
                        id={id}
                        value={form.naics}
                        onChange={(e) => set("naics", e.target.value)}
                        placeholder="e.g. 541512"
                      />
                    )}
                  </Field>
                  <Field label="PSC">
                    {(id) => (
                      <TextInput
                        id={id}
                        value={form.psc}
                        onChange={(e) => set("psc", e.target.value)}
                        placeholder="e.g. D307"
                      />
                    )}
                  </Field>
                  <Field label="Contract vehicle">
                    {(id) => (
                      <TextInput
                        id={id}
                        value={form.contractVehicle}
                        onChange={(e) => set("contractVehicle", e.target.value)}
                        placeholder="e.g. GSA MAS, OASIS+"
                      />
                    )}
                  </Field>
                  <Field label="Questions due">
                    {(id) => (
                      <TextInput
                        id={id}
                        type="date"
                        value={form.questionsDueDate}
                        onChange={(e) => set("questionsDueDate", e.target.value)}
                      />
                    )}
                  </Field>
                  <Field label="Proposal due">
                    {(id) => (
                      <TextInput
                        id={id}
                        type="date"
                        value={form.proposalDueDate}
                        onChange={(e) => set("proposalDueDate", e.target.value)}
                      />
                    )}
                  </Field>
                </>
              )}
            </div>
            <div className="ods-row" style={{ justifyContent: "flex-end" }}>
              <Button size="sm" onClick={create} disabled={saving || !form.title.trim()}>
                {saving ? "Adding…" : "Add opportunity"}
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {state.loading && <LoadingState label="Loading opportunities…" />}
      {state.error && <ErrorState description={state.error.message} onRetry={state.reload} />}
      {state.data && state.data.data.opportunities.length === 0 && (
        <EmptyState title="No opportunities yet" description="Add your first pursuit above." />
      )}
      {state.data && state.data.data.opportunities.length > 0 && (
        <div className="ods-stack">
          {state.data.data.opportunities.map((o) => (
            <Link
              key={o.id}
              to={`/studio/proposal-ai/opportunities/${o.id}`}
              className="launch-card"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <div className="ods-row" style={{ justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{o.title}</div>
                  <span style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)" }}>
                    {o.agency ?? "No agency"} · {o.opportunityNumber ?? "No number"}
                    {o.proposalDueDate ? ` · Due ${o.proposalDueDate}` : ""}
                  </span>
                </div>
                <div className="ods-row">
                  {o.opportunityType ? <Badge tone="neutral">{o.opportunityType}</Badge> : null}
                  {o.setAside ? <Badge tone="neutral">{o.setAside}</Badge> : null}
                  <Badge tone="info">{o.stage}</Badge>
                  {o.estimatedValue ? <span>{formatCurrency(o.estimatedValue, "usd")}</span> : null}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}
